import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ArticleSummary, ProjectSummary } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaBlock } from '@/components/site/cta-block'
import { HomeHero } from '@/components/landing/home/home-hero'
import { HomeAbout } from '@/components/landing/home/home-about'
import { HomeWork } from '@/components/landing/home/home-work'
import { HomeServices } from '@/components/landing/home/home-services'
import { HomeApproach } from '@/components/landing/home/home-approach'
import { HomeResults } from '@/components/landing/home/home-results'
import { HomeStack } from '@/components/landing/home/home-stack'
import { HomeIndex } from '@/components/landing/home/home-index'
import { HomeJournal } from '@/components/landing/home/home-journal'
import { getArticles, getProjects } from '@/lib/content'

interface HomeProps {
    projects: ProjectSummary[]
    articles: ArticleSummary[]
}

const Home: FC<HomeProps> = ({ projects, articles }): ReactNode => {
    return (
        <Layout>
            <Seo description="Zaki Hatta is a software engineer from Padang, Indonesia, building fast web platforms, real-time GIS systems and mobile apps." />

            <HomeHero />
            <HomeAbout />
            <HomeWork projects={projects} />
            <HomeServices />
            <HomeApproach />
            <HomeResults />
            <HomeStack />
            <HomeIndex projects={projects} />
            <HomeJournal articles={articles.slice(0, 3)} />
            <CtaBlock />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    try {
        const [projects, articles] = await Promise.all([getProjects(), getArticles()])

        return { props: { projects, articles }, revalidate: 60 }
    }
    catch (error) {
        console.error('Home page content could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { projects: [], articles: [] }, revalidate: 10 }
    }
}

export default Home
